import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Building2, Loader2, Mail, Plus, Search } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { authApi } from '~/entities/session/api/auth'
import { useAdminBusinesses, useAdminViewBusiness } from '~/entities/session/model/use-admin-auth'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'

export const Route = createFileRoute('/admin/')({
  component: AdminDashboard,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      highlight: (search.highlight as string) || '',
    }
  },
})

function AdminDashboard() {
  const { data: businesses, isLoading, error } = useAdminBusinesses()
  const { mutate: viewBusiness, isPending } = useAdminViewBusiness()
  const [searchQuery, setSearchQuery] = useState('')
  const [resendEmail, setResendEmail] = useState('')
  const [isResending, setIsResending] = useState(false)
  const { highlight } = Route.useSearch()
  const navigate = useNavigate()
  const [highlightedId, setHighlightedId] = useState<string | null>(null)
  const highlightedRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (highlight && businesses) {
      const business = businesses.find((b) => b.legalName === highlight)
      if (business) {
        setHighlightedId(business.id)
        navigate({ to: '/admin', search: {}, replace: true })
        setTimeout(() => setHighlightedId(null), 3000)
      }
    }
  }, [highlight, businesses, navigate])

  useEffect(() => {
    if (highlightedId && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [highlightedId])

  const filteredBusinesses = useMemo(() => {
    if (!businesses) return []

    const sorted = [...businesses].sort((a, b) => a.legalName.localeCompare(b.legalName))
    if (!searchQuery.trim()) return sorted

    const query = searchQuery.toLowerCase().trim()
    return sorted.filter((business) => business.legalName.toLowerCase().includes(query))
  }, [businesses, searchQuery])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="border-blue-600 border-t-4 rounded-full w-12 h-12 animate-spin" />
          <p className="text-slate-600 dark:text-slate-400 text-sm">{m.loadingBusinesses()}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">{m.error()}</CardTitle>
            <CardDescription>{m.failedToLoadBusinesses()}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {error instanceof Error ? error.message : m.unknownError()}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-4 py-8 sm:p-8 sm:py-24">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-8">
          <div>
            <h1 className="mb-2 font-bold text-slate-900 dark:text-slate-100 text-3xl">
              {m.businessManagement()}
            </h1>
            <p className="text-slate-600 dark:text-slate-400">{m.selectBusinessDescription()}</p>
          </div>
          <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto">
            <Link to="/admin/invite-business">
              <Plus className="mr-2 w-4 h-4" />
              {m.createBusiness()}
            </Link>
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <CardTitle className="text-sm">{m.resendConfirmationEmail()}</CardTitle>
            </div>
            <CardDescription className="text-xs">
              {m.resendConfirmationEmailDescription()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="flex gap-2"
              onSubmit={async (e) => {
                e.preventDefault()
                if (!resendEmail.trim()) return
                setIsResending(true)
                try {
                  await authApi.resendVerification(resendEmail.trim())
                  toast.success(m.emailSent(), {
                    description: m.emailSentDescription({
                      email: resendEmail.trim(),
                    }),
                  })
                  setResendEmail('')
                } catch (error) {
                  toast.error(m.failedToSendEmail(), {
                    description: getErrorMessageFromError(error),
                  })
                } finally {
                  setIsResending(false)
                }
              }}
            >
              <Input
                type="email"
                placeholder={m.enterEmailAddress()}
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="flex-1"
                disabled={isResending}
              />
              <Button
                type="submit"
                variant="outline"
                size="default"
                disabled={isResending || !resendEmail.trim()}
              >
                {isResending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                {m.resend()}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="relative mb-6">
          <Search className="top-1/2 left-3 absolute w-5 h-5 text-slate-400 -translate-y-1/2" />
          <Input
            type="text"
            placeholder={m.searchBusinessesPlaceholder()}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-3 bg-white dark:bg-slate-900 px-4 py-3 border border-slate-200 dark:border-slate-800 rounded-lg">
            <div>
              <p className="text-slate-500 dark:text-slate-400 text-xs">{m.totalBusinesses()}</p>
              <p className="font-bold text-slate-900 dark:text-slate-100 text-2xl">
                {businesses?.length || 0}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white dark:bg-slate-900 px-4 py-3 border border-slate-200 dark:border-slate-800 rounded-lg">
            <div>
              <p className="text-slate-500 dark:text-slate-400 text-xs">{m.searchResults()}</p>
              <p className="font-bold text-slate-900 dark:text-slate-100 text-2xl">
                {filteredBusinesses.length}
              </p>
            </div>
          </div>
        </div>

        {filteredBusinesses.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 py-16">
              <Building2 className="w-12 h-12 text-slate-400" />
              <div className="text-center">
                <p className="mb-1 font-medium text-slate-900 dark:text-slate-100">
                  {searchQuery ? m.noBusinessesFound() : m.noBusinessesYet()}
                </p>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  {searchQuery
                    ? m.tryAdjustingYourSearchQuery()
                    : m.businessesWillAppearOnceRegistered()}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-200 dark:divide-slate-800">
                {filteredBusinesses.map((business) => {
                  const isHighlighted = business.id === highlightedId
                  return (
                    <div
                      key={business.id}
                      ref={isHighlighted ? highlightedRef : null}
                      className={cn(
                        'flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 px-4 sm:px-6 py-4 transition-all duration-500',
                        isHighlighted
                          ? 'bg-green-50 dark:bg-green-950/30 ring-2 ring-green-500 ring-inset'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/50',
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <p
                          className={cn(
                            'font-medium truncate',
                            isHighlighted
                              ? 'text-green-700 dark:text-green-300'
                              : 'text-slate-900 dark:text-slate-100',
                          )}
                        >
                          {business.legalName}
                          {isHighlighted && (
                            <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-400">
                              {m.newlyCreated()}
                            </span>
                          )}
                        </p>
                        <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm truncate">
                          {m.businessIdLabel({ id: business.id })}
                        </p>
                      </div>
                      <Button
                        variant={isHighlighted ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => viewBusiness(business)}
                        disabled={isPending}
                        className={cn(
                          'w-full sm:w-auto shrink-0',
                          isPending && 'opacity-50',
                          isHighlighted && 'bg-green-600 hover:bg-green-700 text-white',
                        )}
                      >
                        {m.viewDashboard()}
                      </Button>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
